import type { ReactNode } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import type { ApplianceConnectionStatus, ApplianceHealthLevel, SubscriptionStatus } from '@scada-ink/shared/cloud';

export function PortalHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-text-secondary max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, className = '', padded = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <section className={`glass-panel ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-glass-border">
          <h2 className="text-sm font-semibold">{title}</h2>
          {actions}
        </div>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-text-muted">
      <Loader2 size={16} className="animate-spin" /> {label}
    </div>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <AlertTriangle size={24} className="text-status-fault" />
      <p className="text-sm text-text-secondary max-w-md">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="text-sm font-semibold text-brand-primary">
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, action }: { icon: React.ComponentType<{ size?: number; className?: string }>; title: string; body: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center py-12 px-6">
      <span className="w-12 h-12 rounded-xl bg-ink-muted flex items-center justify-center">
        <Icon size={22} className="text-text-muted" />
      </span>
      <h3 className="mt-4 font-semibold">{title}</h3>
      <div className="mt-1.5 text-sm text-text-secondary max-w-md">{body}</div>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = 'default' }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const color = { default: 'text-text-primary', good: 'text-status-running', warn: 'text-status-idle', bad: 'text-status-fault' }[tone];
  return (
    <div className="glass-card p-4">
      <div className="text-xs text-text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-text-secondary">{sub}</div>}
    </div>
  );
}

/** Usage against an allowance. `max` of 0 means nothing included. */
export function Meter({ label, used, max, unit = '' }: { label: string; used: number; max: number; unit?: string }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : used > 0 ? 100 : 0;
  const color = pct >= 100 ? 'bg-status-fault' : pct >= 80 ? 'bg-status-idle' : 'bg-gradient-to-r from-brand-primary to-brand-secondary';
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-text-secondary">
          <b className="text-text-primary">{used.toLocaleString()}</b> / {max > 0 ? max.toLocaleString() : '—'} {unit}
        </span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-ink-muted overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const STATUS_STYLES: Record<ApplianceConnectionStatus, { label: string; cls: string; dot: string }> = {
  online: { label: 'Online', cls: 'bg-status-running/10 text-status-running', dot: 'bg-status-running' },
  stale: { label: 'Stale', cls: 'bg-status-idle/12 text-status-idle', dot: 'bg-status-idle' },
  offline: { label: 'Offline', cls: 'bg-status-fault/10 text-status-fault', dot: 'bg-status-fault' },
  never: { label: 'Never reported', cls: 'bg-ink-muted text-text-muted', dot: 'bg-status-offline' },
};

export function ConnectionBadge({ status }: { status: ApplianceConnectionStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

const HEALTH_STYLES: Record<ApplianceHealthLevel, { label: string; cls: string }> = {
  healthy: { label: 'Healthy', cls: 'text-status-running' },
  warning: { label: 'Warning', cls: 'text-status-idle' },
  critical: { label: 'Critical', cls: 'text-status-fault' },
  unknown: { label: 'Unknown', cls: 'text-text-muted' },
};

export function HealthLabel({ health }: { health: ApplianceHealthLevel }) {
  const h = HEALTH_STYLES[health];
  return <span className={`text-xs font-semibold ${h.cls}`}>{h.label}</span>;
}

const SUB_STATUS: Record<SubscriptionStatus, { label: string; cls: string }> = {
  active: { label: 'Active', cls: 'bg-status-running/10 text-status-running' },
  trialing: { label: 'Trial', cls: 'bg-brand-primary/10 text-brand-primary' },
  past_due: { label: 'Past due', cls: 'bg-status-fault/10 text-status-fault' },
  incomplete: { label: 'Incomplete', cls: 'bg-status-idle/12 text-status-idle' },
  canceled: { label: 'Canceled', cls: 'bg-ink-muted text-text-muted' },
};

export function SubscriptionBadge({ status }: { status: SubscriptionStatus }) {
  const s = SUB_STATUS[status];
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // A bare date (YYYY-MM-DD) is a calendar day, not an instant: read in the
  // viewer's zone it would show as the day before anywhere west of UTC.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return new Date(dateOnly ? `${iso}T00:00:00Z` : iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * A minimal line chart for a single series. Gaps (null) break the line.
 * Values are plotted against a fixed 0..max range so charts of percentages
 * are comparable.
 */
export function LineChart({
  points,
  max,
  color = '#3b82f6',
  unit = '',
  label,
}: {
  points: Array<{ at: string; value: number | null }>;
  max: number;
  color?: string;
  unit?: string;
  label: string;
}) {
  const W = 300;
  const H = 80;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const latest = values.length ? values[values.length - 1] : null;
  const top = Math.max(max, ...values);
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((p, i) => {
    if (p.value === null) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      return;
    }
    const x = points.length === 1 ? W / 2 : (i / (points.length - 1)) * W;
    const y = H - (p.value / top) * (H - 4) - 2;
    current.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  if (current.length) segments.push(current.join(' '));

  return (
    <div className="glass-card p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-text-muted">{label}</span>
        <span className="text-sm font-bold tabular-nums">{latest === null ? '—' : `${Math.round(latest)}${unit}`}</span>
      </div>
      {values.length === 0 ? (
        <div className="h-20 flex items-center justify-center text-xs text-text-muted">No data</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full h-20" preserveAspectRatio="none" role="img" aria-label={`${label} over the last 24 hours`}>
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="#e5e7ef" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          {segments.map((seg, i) =>
            seg.includes(' ') ? (
              <polyline key={i} points={seg} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
            ) : (
              <circle key={i} cx={seg.split(',')[0]} cy={seg.split(',')[1]} r="2" fill={color} />
            ),
          )}
        </svg>
      )}
    </div>
  );
}

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-text-muted border-b border-glass-border">
            {head.map((h) => (
              <th key={h} className="px-5 py-2.5 font-medium whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-glass-border">{children}</tbody>
      </table>
    </div>
  );
}
